<template>
    <vx-table-row>
        <vx-table-row-title class="break-words">
            <a v-if="props.isAdminOrOwner" :href="userLink" class="no-underline! text-inherit hover:text-sky-600">
                {{ model.id === userId ? 'You' : model.name }}
            </a>
            <template v-else>
                {{ model.id === userId ? 'You' : model.name }}
            </template>
            <div class="text-sm text-slate-500 font-normal mt-1">{{ model.email }}</div>
        </vx-table-row-title>

        <vx-table-row-item v-if="props.hasContactGroups">
            <template v-slot:label> Target Access: </template>

            <div class="flex items-center gap-1">
                <vx-popover v-if="model.role !== 'none'" hover arrow placement="top">
                    <vx-badge size="xs" color="amber" data-test="corp-badge">
                        <font-awesome-icon :icon="faCrown" class="-mx-0.5"></font-awesome-icon>
                    </vx-badge>
                    <template v-slot:content> Corporate Access </template>
                </vx-popover>
                <vx-popover hover arrow placement="top">
                    <vx-badge size="xs" color="violet" data-test="ou-badge">
                        <font-awesome-icon :icon="faBuildingFlag"></font-awesome-icon>
                        {{ orgUnitCount }}
                    </vx-badge>
                    <template v-slot:content> Org Unit Access </template>
                </vx-popover>

                <vx-popover hover arrow placement="top">
                    <vx-badge size="xs" color="slate" data-test="group-badge">
                        <font-awesome-icon :icon="faGrid2Plus" class="-mx-0.5"></font-awesome-icon>
                        {{ groupCount }}
                    </vx-badge>
                    <template v-slot:content> Group Access </template>
                </vx-popover>
            </div>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Team Role: </template>
            <vx-selectable
                v-if="isEditable"
                :modelValue="model.role"
                @update:modelValue="onRoleChange"
                :reduce="(option) => option.value"
                :options="teamRolesOptions({ exclude: ['owner'] })"
                :clearable="false"
                :disabled="updating"
                data-test="role-select"
                class="w-[240px]"
            />
            <template v-else>
                {{ TEAM_USER_ROLES[model.role] || '-' }}
            </template>
        </vx-table-row-item>

        <vx-table-row-buttons v-if="isEditable">
            <vx-dropdown placement="right">
                <vx-button data-test="dropdown" type="button" color="muted" outline class="w-[45px]">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item data-test="delete" @click="confirmDelete">
                        Remove from Team
                        <font-awesome-icon widthAuto :icon="faCircleMinus"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button
                v-if="isEditable && props.hasContactGroups"
                :href="userLink"
                data-test="edit"
                color="muted"
                outline
            >
                Edit <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import {
    faBuildingFlag,
    faCircleMinus,
    faCrown,
    faEllipsis,
    faFilePen,
    faGrid2Plus,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxPopover,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxSelectable,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
    VxSwal,
    VxBadge,
} from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { TEAM_USER_ROLES, teamRolesOptions } from '~/constants/roles';
import { usersClient } from '~/services';
import { computed, ref } from 'vue';
import { catchRequestError, throwSwalRequestError } from '~/utils/request';
import { formatNumber } from '~/utils/helpers';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    isKiosk: {
        type: Boolean,
        default: false,
    },
    hasContactGroups: {
        type: Boolean,
        default: false,
    },
    isAdminOrOwner: {
        type: Boolean,
        default: false,
    },
});

const toasted = useToasted();
const userId = Spark.state.user.id;

const model = defineModel({
    type: Object,
    required: true,
});

const updating = ref(false);
let activeToastId = null;

const emit = defineEmits(['deleted']);

const userLink = computed(() =>
    props.isKiosk
        ? `/kiosk/teams/${props.teamId}/users/${model.value.id}/target-access`
        : `/settings/teams/${props.teamId}/membership/${model.value.id}`
);

const isEditable = computed(
    () => model.value.role !== 'owner' && model.value.id !== userId && props.isAdminOrOwner && !props.readOnly
);

const orgUnitCount = computed(() =>
    ['admin', 'owner'].includes(model.value.role) ? 'All' : formatNumber(model.value.org_unit_count)
);

const groupCount = computed(() =>
    ['admin', 'owner'].includes(model.value.role) ? 'All' : formatNumber(model.value.group_count)
);

const onRoleChange = async (newRole) => {
    if (newRole === model.value.role || updating.value) {
        return;
    }

    const previousRole = model.value.role;
    updating.value = true;

    try {
        await usersClient.updateUser(props.teamId, model.value.id, { role: newRole });
        model.value.role = newRole;

        if (activeToastId !== null) {
            toasted.dismiss(activeToastId);
        }

        const toastConfig = {
            title: `${model.value.name || model.value.email} role updated to ${TEAM_USER_ROLES[newRole]}`,
            type: 'success',
            action: {
                label: 'Undo',
                onClick: async () => {
                    toasted.dismiss(activeToastId);
                    activeToastId = null;
                    try {
                        await usersClient.updateUser(props.teamId, model.value.id, { role: previousRole });
                        model.value.role = previousRole;
                    } catch (e) {
                        toasted.error(catchRequestError(e));
                    }
                },
            },
        };
        activeToastId = toasted.show(toastConfig);
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        updating.value = false;
    }
};

const confirmDelete = () => {
    VxSwal.confirmation('Are you sure?', 'The user will immediately lose access to this team.', 'danger', () =>
        usersClient
            .deleteUser(props.teamId, model.value.id)
            .then(() => {
                emit('deleted', model.value.id);
                toasted.success(`${model.value.name || model.value.email} removed from Team`);
            })
            .catch(throwSwalRequestError)
    );
};
</script>
